import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  IconButton,
  TableSortLabel,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import { Link } from 'react-router-dom';
import moment from 'moment';
import i18next from 'i18next';
import { useQuery } from '@apollo/client';
import { REPORT_STATISTICS_BOOKINGS_QUERY, REPORT_STATISTICS_OFFERS_QUERY } from '../../gql';
import {
  StatisticsReportBookingsFiltersInput,
  HLBookingListOutput,
  ReportStatisticsBookingsQuery,
  EOfferVersionStatus,
} from '__generated__/graphql';

type ReportStatisticsOffersResult = {
  reportStatisticsOffers: Array<{
    id: number;
    refCode: string;
    offerCode: string;
    createdAt: string;
    status: string;
    source: string;
    hotel?: { id: number; name: string; refCode: string; spaceId: number } | null;
    publicCompany?: { id: number; name?: string | null } | null;
    publicClient?: { id: number; firstname?: string | null; lastname?: string | null } | null;
    latestVersionReport?: { id: number; status: string } | null;
    booking?: {
      id?: number;
      refCode?: string;
      responsibleUser?: { id: number; name?: string | null } | null;
      publicClient?: { id: number; firstname?: string | null; lastname?: string | null } | null;
      input?: {
        startDate?: string | null;
        endDate?: string | null;
        days?: Array<{ totalGuests?: number | null; overnightRoomsEZ?: number | null; overnightRoomsDZ?: number | null }> | null;
      } | null;
    } | null;
    history?: Array<{
      id: number;
      status: string;
      createdAt: string;
      user?: { id: number; name?: string | null; spaceId?: number | null } | null;
      publicClient?: { id: number; firstname?: string | null; lastname?: string | null } | null;
    }> | null;
  }>;
};

export type ReportModalEntityType = 'offer' | 'booking';

interface StatisticsBookingsModalProps {
  open: boolean;
  onClose: () => void;
  filters: StatisticsReportBookingsFiltersInput;
  columnLabel: string;
  entityType?: ReportModalEntityType; // 'offer' = show offers (table was offer-based), 'booking' = show bookings
}

type SortField =
  | 'id'
  | 'refCode'
  | 'contactPerson'
  | 'backofficeEmployee'
  | 'company'
  | 'hotel'
  | 'startDate'
  | 'endDate'
  | 'participants'
  | 'overnightStays'
  | 'status'
  | 'cancellation'
  | 'canceledBy'
  | 'type'
  | 'requestDate';

type SortDirection = 'asc' | 'desc';

/**
 * Modal for statistics report drill-down: bookings or offers by entityType.
 */
export default function StatisticsBookingsModal({
  open,
  onClose,
  filters,
  columnLabel,
  entityType = 'booking',
}: StatisticsBookingsModalProps) {
  const [sortField, setSortField] = useState<SortField>('requestDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const isOfferModal = entityType === 'offer';

  const { data: bookingsData, loading: bookingsLoading, error: bookingsError } = useQuery<ReportStatisticsBookingsQuery>(
    REPORT_STATISTICS_BOOKINGS_QUERY,
    {
      variables: { filters },
      skip: !open || isOfferModal,
      fetchPolicy: 'network-only',
    }
  );

  const { data: offersData, loading: offersLoading, error: offersError } = useQuery<ReportStatisticsOffersResult>(
    REPORT_STATISTICS_OFFERS_QUERY as any,
    {
      variables: { filters },
      skip: !open || !isOfferModal,
      fetchPolicy: 'network-only',
    }
  );

  const loading = isOfferModal ? offersLoading : bookingsLoading;
  const error = isOfferModal ? offersError : bookingsError;
  const bookings = (bookingsData?.reportStatisticsBookings || []) as HLBookingListOutput[];
  type OfferRow = ReportStatisticsOffersResult['reportStatisticsOffers'][number];
  const offers = (offersData?.reportStatisticsOffers || []) as OfferRow[];

  const sortedOffers = useMemo(() => {
    return [...offers].sort((a, b) => {
      const aTime = new Date(a.createdAt).getTime();
      const bTime = new Date(b.createdAt).getTime();
      return sortDirection === 'desc' ? bTime - aTime : aTime - bTime;
    });
  }, [offers, sortDirection]);

  const getOfferOvernightStays = (offer: OfferRow): number => {
    return offer.booking?.input?.days?.reduce((sum, d) => sum + (d.overnightRoomsEZ || 0) + (d.overnightRoomsDZ || 0), 0) || 0;
  };

  const getOfferParticipants = (offer: OfferRow): number => {
    return offer.booking?.input?.days?.reduce((sum, d) => sum + (d.totalGuests || 0), 0) || 0;
  };

  const getOfferBackofficeEmployeeName = (offer: OfferRow): string => {
    // Prefer the directly assigned responsible user on the booking — reliable regardless of offer state
    if (offer.booking?.responsibleUser?.name) {
      return offer.booking.responsibleUser.name;
    }
    const earliestHistoryWithUser = [...(offer.history || [])]
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      .find(h => h.user && h.user.spaceId === null);
    return earliestHistoryWithUser?.user?.name || '-';
  };

  const getOfferCancellationStatus = (offer: OfferRow): string => {
    const cancellationHistory = (offer.history || []).find(
      h => h.status === 'CANCELLED_CLIENT' || h.status === 'INTEGRATION_BOOKING_CANCELED'
    );
    return cancellationHistory ? 'Yes' : 'No';
  };

  const getOfferCanceledBy = (offer: OfferRow): string => {
    const cancellationHistory = (offer.history || []).find(
      h => h.status === 'CANCELLED_CLIENT' || h.status === 'INTEGRATION_BOOKING_CANCELED'
    );
    if (!cancellationHistory) return '-';
    if (cancellationHistory.status === 'CANCELLED_CLIENT') return 'booker';
    if (cancellationHistory.user && cancellationHistory.user.spaceId !== null) return 'hotel';
    if (cancellationHistory.publicClient) return 'booker';
    return 'hotel';
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Moved before sortedBookings useMemo to avoid temporal dead zone when sorting by these fields
  const getBackofficeEmployeeName = (booking: HLBookingListOutput): string => {
    // Prefer the directly assigned responsible user — reliable regardless of offer state
    if (booking.responsibleUser?.name) {
      return booking.responsibleUser.name;
    }

    // Fallback: find the earliest Seminargo user (spaceId === null) in offer history.
    // This covers legacy bookings that predate the responsibleUserId field.
    if (booking.offers && booking.offers.length > 0) {
      for (const offer of booking.offers) {
        if (offer.history && offer.history.length > 0) {
          const earliestHistoryWithUser = [...offer.history]
            .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
            .find(h => h.user && h.user.spaceId === null);
          if (earliestHistoryWithUser?.user) {
            return earliestHistoryWithUser.user.name || '-';
          }
        }
      }
    }
    return '-';
  };

  const getCancellationStatus = (booking: HLBookingListOutput): string => {
    // Check all offers for cancellation history
    if (booking.offers && booking.offers.length > 0) {
      for (const offer of booking.offers) {
        if (offer.history && offer.history.length > 0) {
          const cancellationHistory = offer.history.find(
            h => h.status === 'CANCELLED_CLIENT' || h.status === 'INTEGRATION_BOOKING_CANCELED'
          );
          if (cancellationHistory) {
            return String(i18next.t('filter-cancellation-yes'));
          }
        }
      }
    }
    return String(i18next.t('filter-cancellation-no'));
  };

  const getCanceledBy = (booking: HLBookingListOutput): string => {
    // Check all offers for cancellation history
    if (booking.offers && booking.offers.length > 0) {
      for (const offer of booking.offers) {
        if (offer.history && offer.history.length > 0) {
          const cancellationHistory = offer.history.find(
            h => h.status === 'CANCELLED_CLIENT' || h.status === 'INTEGRATION_BOOKING_CANCELED'
          );
          if (cancellationHistory) {
            if (cancellationHistory.status === 'CANCELLED_CLIENT') {
              return 'booker';
            } else if (cancellationHistory.status === 'INTEGRATION_BOOKING_CANCELED') {
              // Check if it was canceled by hotel (user with spaceId) or client (publicClientId)
              if (cancellationHistory.user && cancellationHistory.user.spaceId !== null) {
                return 'hotel';
              } else if (cancellationHistory.publicClient) {
                return 'booker';
              }
              return 'hotel'; // Default to hotel for integration cancellations
            }
          }
        }
      }
    }
    return '-';
  };

  const sortedBookings = useMemo(() => {
    return [...bookings].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'refCode':
          aValue = a.refCode;
          bValue = b.refCode;
          break;
        case 'contactPerson':
          aValue = `${a.publicClient?.firstname || ''} ${a.publicClient?.lastname || ''}`.trim();
          bValue = `${b.publicClient?.firstname || ''} ${b.publicClient?.lastname || ''}`.trim();
          break;
        case 'backofficeEmployee':
          aValue = getBackofficeEmployeeName(a);
          bValue = getBackofficeEmployeeName(b);
          break;
        case 'company':
          aValue = a.publicCompany?.name || '';
          bValue = b.publicCompany?.name || '';
          break;
        case 'hotel': {
          const aHotels = a.offers?.map(o => o.hotel?.name).filter(Boolean) || [];
          const bHotels = b.offers?.map(o => o.hotel?.name).filter(Boolean) || [];
          aValue = aHotels.length === 1 ? aHotels[0] : '';
          bValue = bHotels.length === 1 ? bHotels[0] : '';
          break;
        }
        case 'startDate':
          aValue = a.input?.startDate ? new Date(a.input.startDate).getTime() : 0;
          bValue = b.input?.startDate ? new Date(b.input.startDate).getTime() : 0;
          break;
        case 'endDate':
          aValue = a.input?.endDate ? new Date(a.input.endDate).getTime() : 0;
          bValue = b.input?.endDate ? new Date(b.input.endDate).getTime() : 0;
          break;
        case 'participants': {
          const aParticipants = a.input?.days?.reduce((sum, d) => sum + (d.totalGuests || 0), 0) || 0;
          const bParticipants = b.input?.days?.reduce((sum, d) => sum + (d.totalGuests || 0), 0) || 0;
          aValue = aParticipants;
          bValue = bParticipants;
          break;
        }
        case 'overnightStays': {
          const aNights = a.input?.days?.reduce((sum, d) => sum + (d.overnightRoomsEZ || 0) + (d.overnightRoomsDZ || 0), 0) || 0;
          const bNights = b.input?.days?.reduce((sum, d) => sum + (d.overnightRoomsEZ || 0) + (d.overnightRoomsDZ || 0), 0) || 0;
          aValue = aNights;
          bValue = bNights;
          break;
        }
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'cancellation':
          aValue = getCancellationStatus(a) === 'Yes' ? 1 : 0;
          bValue = getCancellationStatus(b) === 'Yes' ? 1 : 0;
          break;
        case 'canceledBy':
          aValue = getCanceledBy(a);
          bValue = getCanceledBy(b);
          break;
        case 'type': {
          const aHasOvernight = (a.input?.days?.some(d => ((d.overnightRoomsEZ || 0) + (d.overnightRoomsDZ || 0)) > 0) || false);
          const bHasOvernight = (b.input?.days?.some(d => ((d.overnightRoomsEZ || 0) + (d.overnightRoomsDZ || 0)) > 0) || false);
          aValue = aHasOvernight ? 'seminar_with_overnight' : 'day_seminar';
          bValue = bHasOvernight ? 'seminar_with_overnight' : 'day_seminar';
          break;
        }
        case 'requestDate':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [bookings, sortField, sortDirection]);

  const handleExportOffers = () => {
    const headers = [
      String(i18next.t('modal-offer-id')),
      String(i18next.t('modal-ref-code')),
      String(i18next.t('contact-person')),
      String(i18next.t('modal-backoffice-employee')),
      String(i18next.t('statistics-hotel')),
      String(i18next.t('modal-company')),
      String(i18next.t('modal-start-date')),
      String(i18next.t('modal-end-date')),
      String(i18next.t('statistics-participants')),
      String(i18next.t('statistics-overnights')),
      String(i18next.t('modal-cancellation')),
      String(i18next.t('modal-canceled-by')),
      String(i18next.t('contracts-header-status')),
      String(i18next.t('modal-request-date')),
    ];
    const csvData = sortedOffers.map((offer) => [
      offer.id,
      offer.refCode || offer.offerCode || '',
      `${offer.publicClient?.firstname || offer.booking?.publicClient?.firstname || ''} ${offer.publicClient?.lastname || offer.booking?.publicClient?.lastname || ''}`.trim() || '-',
      getOfferBackofficeEmployeeName(offer),
      offer.hotel?.name ?? '',
      offer.publicCompany?.name ?? '',
      offer.booking?.input?.startDate ? moment(offer.booking.input.startDate).format('YYYY-MM-DD') : '',
      offer.booking?.input?.endDate ? moment(offer.booking.input.endDate).format('YYYY-MM-DD') : '',
      getOfferParticipants(offer),
      getOfferOvernightStays(offer),
      getOfferCancellationStatus(offer),
      getOfferCanceledBy(offer),
      offer.latestVersionReport?.status
        ? i18next.t(`enums-EOfferVersionStatus-${offer.latestVersionReport.status}`)
        : offer.status
          ? i18next.t(`enums-EOfferStatus-${offer.status}`)
          : '',
      moment(offer.createdAt).format('YYYY-MM-DD'),
    ]);
    const csvContent = [headers, ...csvData]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `statistics-offers-${moment().format('YYYY-MM-DD')}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleExportExcel = () => {
    // Headers
    const headers = [
      String(i18next.t('modal-booking-id')),
      String(i18next.t('contact-person')),
      String(i18next.t('modal-backoffice-employee')),
      String(i18next.t('modal-company')),
      String(i18next.t('statistics-hotel')),
      String(i18next.t('modal-start-date')),
      String(i18next.t('modal-end-date')),
      String(i18next.t('statistics-participants')),
      String(i18next.t('statistics-overnights')),
      String(i18next.t('contracts-header-status')),
      String(i18next.t('modal-cancellation')),
      String(i18next.t('modal-canceled-by')),
      String(i18next.t('modal-type')),
      String(i18next.t('modal-request-date')),
    ];

    // Data rows
    const csvData = sortedBookings.map(booking => {
      const hotels = booking.offers?.map(o => o.hotel?.name).filter(Boolean) || [];
      const hotelName = hotels.length === 1 ? hotels[0] : '';
      const hasOvernight = booking.input?.days?.some(d => ((d.overnightRoomsEZ || 0) + (d.overnightRoomsDZ || 0)) > 0) || false;
      const overnightStays = booking.input?.days?.reduce((sum, d) => sum + (d.overnightRoomsEZ || 0) + (d.overnightRoomsDZ || 0), 0) || 0;
      const participants = booking.input?.days?.reduce((sum, d) => sum + (d.totalGuests || 0), 0) || 0;

      return [
        booking.refCode,
        `${booking.publicClient?.firstname || ''} ${booking.publicClient?.lastname || ''}`.trim(),
        getBackofficeEmployeeName(booking),
        booking.publicCompany?.name || '',
        hotelName,
        booking.input?.startDate ? moment(booking.input.startDate).format('YYYY-MM-DD') : '',
        booking.input?.endDate ? moment(booking.input.endDate).format('YYYY-MM-DD') : '',
        participants,
        overnightStays,
        getBookingStatus(booking),
        getCancellationStatus(booking),
        getCanceledBy(booking),
        hasOvernight ? String(i18next.t('modal-type-seminar-overnight')) : String(i18next.t('modal-type-day-seminar')),
        moment(booking.createdAt).format('YYYY-MM-DD'),
      ];
    });

    // Create CSV content
    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    // Create blob and download
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `statistics-bookings-${moment().format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Pick the offer that actually matched the column filter criteria,
  // so hotel and status reflect the relevant offer rather than offers[0].
  const findRelevantOffer = (booking: HLBookingListOutput) => {
    const { columnType } = filters;
    const filterByReservationDate = (filters as { filterByReservationDate?: boolean }).filterByReservationDate;
    const offers = booking.offers || [];

    if (filterByReservationDate) {
      return offers.find(o => o.history?.some(h => h.status === 'RESERVATION_HOTEL')) ?? offers[0] ?? null;
    }

    if (columnType === 'approvedCount' || columnType === 'totalNights' || columnType === 'totalGuests') {
      const approvedStatuses = ['APPROVED', 'IV_APPROVED', 'IV_WAITFORHOTEL', 'IV_WAITFORSEMAPPROVAL'];
      return (
        offers.find(o => {
          const status = o.latestVersionReport?.status;
          if (!status || !approvedStatuses.includes(status)) return false;
          const hasCancellation = o.history?.some(h => h.status === 'CANCELLED_CLIENT' || h.status === 'INTEGRATION_BOOKING_CANCELED');
          return !hasCancellation;
        }) ?? offers[0]
      );
    }

    if (columnType === 'rejectedByHotelCount') {
      return offers.find(o => ['REJECTED_HOTEL', 'REJECTED_HOTEL_WITHDRAWN'].includes(o.latestVersionReport?.status ?? '')) ?? offers[0];
    }

    if (columnType === 'rejectedByClientCount') {
      return offers.find(o => o.latestVersionReport?.status === 'REJECTED_CLIENT') ?? offers[0];
    }

    return offers[0] ?? null;
  };

  const getHotelName = (booking: HLBookingListOutput): string => {
    return findRelevantOffer(booking)?.hotel?.name ?? '';
  };

  const getHotelId = (booking: HLBookingListOutput): number | null => {
    return findRelevantOffer(booking)?.hotel?.id ?? null;
  };

  const getHotelSpaceId = (booking: HLBookingListOutput): number | null => {
    return findRelevantOffer(booking)?.hotel?.spaceId ?? null;
  };

  const getOvernightStays = (booking: HLBookingListOutput): number => {
    return booking.input?.days?.reduce((sum, d) => sum + (d.overnightRoomsEZ || 0) + (d.overnightRoomsDZ || 0), 0) || 0;
  };

  const getParticipants = (booking: HLBookingListOutput): number => {
    return booking.input?.days?.reduce((sum, d) => sum + (d.totalGuests || 0), 0) || 0;
  };

  const getBookingType = (booking: HLBookingListOutput): string => {
    const hasOvernight = booking.input?.days?.some(d => ((d.overnightRoomsEZ || 0) + (d.overnightRoomsDZ || 0)) > 0) || false;
    return hasOvernight ? String(i18next.t('modal-type-seminar-overnight')) : String(i18next.t('modal-type-day-seminar'));
  };

  const getBookingStatus = (booking: HLBookingListOutput): string => {
    const offer = findRelevantOffer(booking);
    if (offer) {
      if (offer.latestVersionReport?.status) {
        return i18next.t(`enums-EOfferVersionStatus-${offer.latestVersionReport.status}`);
      }
      if (offer.status) {
        return i18next.t(`enums-EOfferStatus-${offer.status}`);
      }
    }
    return i18next.t(`enums-EHLBookingStatus-${booking.status}`);
  };

  const entityLabel = isOfferModal ? String(i18next.t('modal-offers')) : String(i18next.t('modal-bookings'));
  const count = isOfferModal ? sortedOffers.length : sortedBookings.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {entityLabel} - {columnLabel}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: '#AC2A6E' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Box p={2}>
            <Typography color="error">{i18next.t('modal-load-error')} {entityLabel.toLowerCase()}: {error.message}</Typography>
          </Box>
        )}
        {!loading && !error && isOfferModal && (
          <>
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                {count} {i18next.t('modal-offers-found')}
              </Typography>
            </Box>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      'modal-offer-id',
                      'modal-ref-code',
                      'contact-person',
                      'modal-backoffice-employee',
                      'statistics-hotel',
                      'modal-company',
                      'modal-start-date',
                      'modal-end-date',
                      'statistics-participants',
                      'statistics-overnights',
                      'modal-cancellation',
                      'modal-canceled-by',
                      'contracts-header-status',
                      'modal-request-date',
                    ].map((key) => (
                      <TableCell key={key} sx={{ color: '#AC2A6E', fontWeight: 600 }}>
                        {String(i18next.t(key))}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedOffers.map((offer) => (
                    <TableRow key={offer.id}>
                      <TableCell>{offer.id}</TableCell>
                      <TableCell>
                        <Link to={`/offers/${offer.id}`} style={{ textDecoration: 'none', color: '#AC2A6E' }}>
                          {offer.refCode || offer.offerCode || '-'}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {`${offer.publicClient?.firstname || offer.booking?.publicClient?.firstname || ''} ${offer.publicClient?.lastname || offer.booking?.publicClient?.lastname || ''}`.trim() || '-'}
                      </TableCell>
                      <TableCell>{getOfferBackofficeEmployeeName(offer)}</TableCell>
                      <TableCell>
                        {offer.hotel?.id ? (
                          <Link
                            to={`/settings/hotels/${offer.hotel.id}`}
                            style={{ textDecoration: 'none', color: '#AC2A6E', cursor: 'pointer' }}
                          >
                            {offer.hotel.name}
                          </Link>
                        ) : (
                          offer.hotel?.name ?? '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {offer.publicCompany?.id ? (
                          <Link
                            to={`/clients/companies/${offer.publicCompany.id}`}
                            style={{ textDecoration: 'none', color: '#AC2A6E' }}
                          >
                            {offer.publicCompany.name ?? '-'}
                          </Link>
                        ) : (
                          offer.publicCompany?.name ?? '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {offer.booking?.input?.startDate ? moment(offer.booking.input.startDate).format('YYYY-MM-DD') : '-'}
                      </TableCell>
                      <TableCell>
                        {offer.booking?.input?.endDate ? moment(offer.booking.input.endDate).format('YYYY-MM-DD') : '-'}
                      </TableCell>
                      <TableCell>{getOfferParticipants(offer)}</TableCell>
                      <TableCell>{getOfferOvernightStays(offer)}</TableCell>
                      <TableCell>{getOfferCancellationStatus(offer)}</TableCell>
                      <TableCell>{getOfferCanceledBy(offer)}</TableCell>
                      <TableCell>
                        {offer.latestVersionReport?.status
                          ? i18next.t(`enums-EOfferVersionStatus-${offer.latestVersionReport.status}`)
                          : offer.status
                            ? i18next.t(`enums-EOfferStatus-${offer.status}`)
                            : '-'}
                      </TableCell>
                      <TableCell>{moment(offer.createdAt).format('YYYY-MM-DD')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
        {!loading && !error && !isOfferModal && (
          <>
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                {count} {i18next.t('modal-bookings-found')}
              </Typography>
            </Box>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'id'}
                        direction={sortField === 'id' ? sortDirection : 'asc'}
                        onClick={() => handleSort('id')}
                        sx={{ color: '#AC2A6E', '&:hover': { color: '#AC2A6E' } }}
                      >
                        {i18next.t('modal-booking-id')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'contactPerson'}
                        direction={sortField === 'contactPerson' ? sortDirection : 'asc'}
                        onClick={() => handleSort('contactPerson')}
                        sx={{ color: '#AC2A6E', '&:hover': { color: '#AC2A6E' } }}
                      >
                        {i18next.t('contact-person')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'backofficeEmployee'}
                        direction={sortField === 'backofficeEmployee' ? sortDirection : 'asc'}
                        onClick={() => handleSort('backofficeEmployee')}
                        sx={{ color: '#AC2A6E', '&:hover': { color: '#AC2A6E' } }}
                      >
                        {i18next.t('modal-backoffice-employee')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'company'}
                        direction={sortField === 'company' ? sortDirection : 'asc'}
                        onClick={() => handleSort('company')}
                        sx={{ color: '#AC2A6E', '&:hover': { color: '#AC2A6E' } }}
                      >
                        {i18next.t('modal-company')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'hotel'}
                        direction={sortField === 'hotel' ? sortDirection : 'asc'}
                        onClick={() => handleSort('hotel')}
                        sx={{ color: '#AC2A6E', '&:hover': { color: '#AC2A6E' } }}
                      >
                        {i18next.t('statistics-hotel')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'startDate'}
                        direction={sortField === 'startDate' ? sortDirection : 'asc'}
                        onClick={() => handleSort('startDate')}
                        sx={{ color: '#AC2A6E', '&:hover': { color: '#AC2A6E' } }}
                      >
                        {i18next.t('modal-start-date')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'endDate'}
                        direction={sortField === 'endDate' ? sortDirection : 'asc'}
                        onClick={() => handleSort('endDate')}
                        sx={{ color: '#AC2A6E', '&:hover': { color: '#AC2A6E' } }}
                      >
                        {i18next.t('modal-end-date')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'participants'}
                        direction={sortField === 'participants' ? sortDirection : 'asc'}
                        onClick={() => handleSort('participants')}
                        sx={{ color: '#AC2A6E', '&:hover': { color: '#AC2A6E' } }}
                      >
                        {i18next.t('statistics-participants')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'overnightStays'}
                        direction={sortField === 'overnightStays' ? sortDirection : 'asc'}
                        onClick={() => handleSort('overnightStays')}
                        sx={{ color: '#AC2A6E', '&:hover': { color: '#AC2A6E' } }}
                      >
                        {i18next.t('statistics-overnights')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'status'}
                        direction={sortField === 'status' ? sortDirection : 'asc'}
                        onClick={() => handleSort('status')}
                        sx={{ color: '#AC2A6E', '&:hover': { color: '#AC2A6E' } }}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'cancellation'}
                        direction={sortField === 'cancellation' ? sortDirection : 'asc'}
                        onClick={() => handleSort('cancellation')}
                        sx={{ color: '#AC2A6E', '&:hover': { color: '#AC2A6E' } }}
                      >
                        {i18next.t('modal-cancellation')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'canceledBy'}
                        direction={sortField === 'canceledBy' ? sortDirection : 'asc'}
                        onClick={() => handleSort('canceledBy')}
                        sx={{ color: '#AC2A6E', '&:hover': { color: '#AC2A6E' } }}
                      >
                        {i18next.t('modal-canceled-by')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'type'}
                        direction={sortField === 'type' ? sortDirection : 'asc'}
                        onClick={() => handleSort('type')}
                        sx={{ color: '#AC2A6E', '&:hover': { color: '#AC2A6E' } }}
                      >
                        {i18next.t('modal-type')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'requestDate'}
                        direction={sortField === 'requestDate' ? sortDirection : 'asc'}
                        onClick={() => handleSort('requestDate')}
                        sx={{ color: '#AC2A6E', '&:hover': { color: '#AC2A6E' } }}
                      >
                        {i18next.t('modal-request-date')}
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedBookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell>
                        <Link to={`/bookings/${booking.id}`} style={{ textDecoration: 'none', color: '#AC2A6E' }}>
                          {booking.refCode}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {booking.publicClient ? (
                          <Link
                            to={`/clients/clients/${booking.publicClient.id}`}
                            onClick={() => onClose()}
                            style={{ textDecoration: 'none', color: '#AC2A6E' }}
                          >
                            {`${booking.publicClient.firstname || ''} ${booking.publicClient.lastname || ''}`.trim() || '-'}
                          </Link>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{getBackofficeEmployeeName(booking)}</TableCell>
                      <TableCell>
                        {booking.publicCompany ? (
                          <Link
                            to={`/clients/companies/${booking.publicCompany.id}`}
                            onClick={() => onClose()}
                            style={{ textDecoration: 'none', color: '#AC2A6E' }}
                          >
                            {booking.publicCompany.name || '-'}
                          </Link>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {getHotelId(booking) ? (
                          <Link
                            to={`/settings/hotels/${getHotelId(booking)!}`}
                            style={{ textDecoration: 'none', color: '#AC2A6E', cursor: 'pointer' }}
                          >
                            {getHotelName(booking)}
                          </Link>
                        ) : getHotelName(booking) ? (
                          getHotelName(booking)
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {booking.input?.startDate ? moment(booking.input.startDate).format('YYYY-MM-DD') : '-'}
                      </TableCell>
                      <TableCell>
                        {booking.input?.endDate ? moment(booking.input.endDate).format('YYYY-MM-DD') : '-'}
                      </TableCell>
                      <TableCell>{getParticipants(booking)}</TableCell>
                      <TableCell>{getOvernightStays(booking)}</TableCell>
                      <TableCell>
                        {getBookingStatus(booking)}
                      </TableCell>
                      <TableCell>{getCancellationStatus(booking)}</TableCell>
                      <TableCell>{getCanceledBy(booking)}</TableCell>
                      <TableCell>{getBookingType(booking)}</TableCell>
                      <TableCell>{moment(booking.createdAt).format('YYYY-MM-DD')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: '#AC2A6E' }}>{i18next.t('close')}</Button>
        <Button
          variant="contained"
          startIcon={<GetAppIcon />}
          onClick={isOfferModal ? handleExportOffers : handleExportExcel}
          disabled={loading || count === 0}
          sx={{ backgroundColor: '#AC2A6E', '&:hover': { backgroundColor: '#8a1f56' } }}
        >
          {i18next.t('download-csv-report')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

